import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

// Type helpers
type Interview = Database['public']['Tables']['interviews']['Row'];
type InterviewInsert = Database['public']['Tables']['interviews']['Insert'];
type InterviewUpdate = Database['public']['Tables']['interviews']['Update'];

type ResearchData = Database['public']['Tables']['research_data']['Row'];
type ResearchDataInsert = Database['public']['Tables']['research_data']['Insert'];

type EmergencyPlan = Database['public']['Tables']['emergency_plans']['Row'];
type EmergencyPlanInsert = Database['public']['Tables']['emergency_plans']['Insert'];

type GOAPExecution = Database['public']['Tables']['goap_executions']['Row'];
type GOAPExecutionInsert = Database['public']['Tables']['goap_executions']['Insert'];
type GOAPExecutionUpdate = Database['public']['Tables']['goap_executions']['Update'];

/**
 * Interview Queries
 */
export const interviewQueries = {
  // Create new interview
  async create(data: InterviewInsert): Promise<Interview> {
    const { data: interview, error } = await supabase
      .from('interviews')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return interview;
  },

  // Get interview by ID
  async getById(id: string): Promise<Interview | null> {
    const { data, error } = await supabase
      .from('interviews')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Get all interviews for current user
  async getAll(): Promise<Interview[]> {
    const { data, error } = await supabase
      .from('interviews')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Update interview
  async update(id: string, updates: InterviewUpdate): Promise<Interview> {
    const { data, error } = await supabase
      .from('interviews')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Mark interview as completed
  async complete(id: string): Promise<Interview> {
    return this.update(id, {
      status: 'completed',
      completed_at: new Date().toISOString(),
    });
  },

  /**
   * Get latest interview for a user (for resuming)
   * Returns most recent interview, prioritizing in-progress over completed
   */
  async getLatestByUserId(userId: string): Promise<Interview | null> {
    const { data, error } = await supabase
      .from('interviews')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data;
  },
};

/**
 * Research Data Queries
 */
export const researchQueries = {
  // Store research result
  async store(data: ResearchDataInsert): Promise<ResearchData> {
    const { data: research, error } = await supabase
      .from('research_data')
      .upsert(data, {
        onConflict: 'interview_id,agent_type',
      })
      .select()
      .single();

    if (error) throw error;
    return research;
  },

  // Get all research for an interview
  async getByInterviewId(interviewId: string): Promise<ResearchData[]> {
    const { data, error } = await supabase
      .from('research_data')
      .select('*')
      .eq('interview_id', interviewId);

    if (error) throw error;
    return data || [];
  },

  // Get specific agent's research
  async getByAgentType(
    interviewId: string,
    agentType: string
  ): Promise<ResearchData | null> {
    const { data, error } = await supabase
      .from('research_data')
      .select('*')
      .eq('interview_id', interviewId)
      .eq('agent_type', agentType)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return data;
  },
};

/**
 * Emergency Plan Queries
 */
export const planQueries = {
  // Create new plan
  async create(data: EmergencyPlanInsert): Promise<EmergencyPlan> {
    const { data: plan, error } = await supabase
      .from('emergency_plans')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return plan;
  },

  // Get latest plan for interview
  async getLatest(interviewId: string): Promise<EmergencyPlan | null> {
    const { data, error } = await supabase
      .from('emergency_plans')
      .select('*')
      .eq('interview_id', interviewId)
      .eq('is_latest', true)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  // Get all plans for user
  async getAll(): Promise<EmergencyPlan[]> {
    const { data, error } = await supabase
      .from('emergency_plans')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Update PDF URL after generation
  async updatePdfUrl(
    planId: string,
    pdfUrl: string,
    metadata?: { file_size?: number; page_count?: number }
  ): Promise<EmergencyPlan> {
    const { data, error } = await supabase
      .from('emergency_plans')
      .update({
        pdf_url: pdfUrl,
        pdf_metadata: {
          ...metadata,
          generated_at: new Date().toISOString(),
        },
      })
      .eq('id', planId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};

/**
 * GOAP Execution Queries
 *
 * Manages workflow execution state for emergency plan generation.
 * Tracks progress, current action, errors, and completion status.
 *
 * GOAP State Machine:
 * - Initial State: { interview_complete: true }
 * - Goal State: { pdf_delivered: true }
 * - Actions: validate → research → compile → generate → deliver
 *
 * Usage:
 * 1. Create execution at workflow start
 * 2. Update progress as each action completes
 * 3. Mark complete or failed at workflow end
 */
export const goapQueries = {
  /**
   * Create new GOAP execution for an interview
   *
   * @param data - GOAP execution data (interview_id, initial_state, goal_state, action_plan)
   * @returns Created execution record
   *
   * Example:
   * ```typescript
   * const execution = await goapQueries.create({
   *   interview_id: 'abc-123',
   *   initial_state: { interview_complete: true },
   *   goal_state: { pdf_delivered: true },
   *   action_plan: [
   *     { name: 'validate_interview', description: 'Validate interview data' },
   *     { name: 'execute_research', description: 'Research location risks' },
   *     // ... other actions
   *   ],
   * });
   * ```
   */
  async create(data: GOAPExecutionInsert): Promise<GOAPExecution> {
    const { data: execution, error } = await supabase
      .from('goap_executions')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return execution;
  },

  /**
   * Get GOAP execution for an interview
   *
   * @param interviewId - Interview ID to look up
   * @returns Execution record or null if not found
   *
   * Used by PlanGeneration.tsx to poll workflow progress
   */
  async getByInterviewId(interviewId: string): Promise<GOAPExecution | null> {
    const { data, error } = await supabase
      .from('goap_executions')
      .select('*')
      .eq('interview_id', interviewId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return data;
  },

  /**
   * Update GOAP execution state
   *
   * @param interviewId - Interview ID
   * @param updates - Fields to update (current_state, current_action, progress, etc.)
   * @returns Updated execution record
   */
  async update(
    interviewId: string,
    updates: GOAPExecutionUpdate
  ): Promise<GOAPExecution> {
    const { data, error } = await supabase
      .from('goap_executions')
      .update(updates)
      .eq('interview_id', interviewId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update progress percentage only
   *
   * @param interviewId - Interview ID
   * @param progress - Progress percentage (0-100)
   *
   * Quick helper for updating progress bar without full state update
   */
  async updateProgress(
    interviewId: string,
    progress: number
  ): Promise<GOAPExecution> {
    return this.update(interviewId, {
      progress_percentage: Math.min(100, Math.max(0, progress)),
    });
  },

  /**
   * Start an action (update current_action and status)
   *
   * @param interviewId - Interview ID
   * @param actionName - Name of action to start (e.g., 'execute_research')
   * @param newState - New world state after action completes
   *
   * Example:
   * ```typescript
   * await goapQueries.startAction(
   *   'abc-123',
   *   'execute_research',
   *   { interview_complete: true, location_data: true }
   * );
   * ```
   */
  async startAction(
    interviewId: string,
    actionName: string,
    newState?: Record<string, unknown>
  ): Promise<GOAPExecution> {
    const updates: GOAPExecutionUpdate = {
      current_action: actionName,
      status: 'running',
    };

    if (newState) {
      updates.current_state = JSON.parse(JSON.stringify(newState)) as any;
    }

    return this.update(interviewId, updates);
  },

  /**
   * Mark action as completed and move to next
   *
   * @param interviewId - Interview ID
   * @param actionName - Name of completed action
   * @param newState - New world state after action completes
   * @returns Updated execution record
   *
   * Automatically calculates progress percentage based on completed actions
   *
   * Example:
   * ```typescript
   * await goapQueries.completeAction(
   *   'abc-123',
   *   'execute_research',
   *   { interview_complete: true, research_complete: true }
   * );
   * ```
   */
  async completeAction(
    interviewId: string,
    actionName: string,
    newState: Record<string, unknown>
  ): Promise<GOAPExecution> {
    const current = await this.getByInterviewId(interviewId);
    if (!current) throw new Error('GOAP execution not found');

    // Add to completed actions list
    const completedActions = [...(current.completed_actions || []), actionName];

    // Calculate progress based on completed vs total actions
    const totalActions = (current.action_plan as any[])?.length || 1;
    const progressPercentage = Math.round((completedActions.length / totalActions) * 100);

    return this.update(interviewId, {
      current_state: newState,
      completed_actions: completedActions,
      current_action: null, // Clear current action
      progress_percentage: progressPercentage,
    });
  },

  /**
   * Mark entire workflow as completed
   *
   * @param interviewId - Interview ID
   * @returns Updated execution record with status='completed'
   *
   * Call this when the final action (deliver_plan) succeeds
   */
  async complete(interviewId: string): Promise<GOAPExecution> {
    return this.update(interviewId, {
      status: 'completed',
      completed_at: new Date().toISOString(),
      progress_percentage: 100,
      current_action: null,
    });
  },

  /**
   * Mark workflow as failed with error message
   *
   * @param interviewId - Interview ID
   * @param error - Error message to store
   * @returns Updated execution record with status='failed'
   *
   * Increments error_count for retry logic
   * Stores last_error for user display
   */
  async fail(interviewId: string, error: string): Promise<GOAPExecution> {
    const current = await this.getByInterviewId(interviewId);
    if (!current) throw new Error('GOAP execution not found');

    return this.update(interviewId, {
      status: 'failed',
      error_count: (current.error_count || 0) + 1,
      last_error: error,
    });
  },

  /**
   * Delete GOAP execution (for cleanup or retry)
   *
   * @param interviewId - Interview ID
   *
   * Use when user wants to restart workflow from scratch
   */
  async delete(interviewId: string): Promise<void> {
    const { error } = await supabase
      .from('goap_executions')
      .delete()
      .eq('interview_id', interviewId);

    if (error) throw error;
  },
};

/**
 * Storage Helpers
 */
export const storageHelpers = {
  // Upload interview image
  async uploadInterviewImage(
    userId: string,
    interviewId: string,
    file: File
  ): Promise<string> {
    const filePath = `${userId}/${interviewId}/${file.name}`;

    const { error } = await supabase.storage
      .from('interview-images')
      .upload(filePath, file);

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('interview-images')
      .getPublicUrl(filePath);

    return publicUrl;
  },

  // Upload PDF plan
  async uploadPlanPdf(
    userId: string,
    planId: string,
    pdfBlob: Blob
  ): Promise<string> {
    const filePath = `${userId}/${planId}.pdf`;

    const { error } = await supabase.storage
      .from('emergency-plans')
      .upload(filePath, pdfBlob, {
        contentType: 'application/pdf',
      });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('emergency-plans')
      .getPublicUrl(filePath);

    return publicUrl;
  },
};
